import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PackageCheck } from 'lucide-react'
import { useMemo, useState, type ReactNode, type SyntheticEvent } from 'react'

import type { PurchaseDetail } from '@aeris/contracts'
import { formatMoney, ZERO_MONEY } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import { checkReceipt, parseQuantity } from '@aeris/purchasing'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ApiError, receivePurchase } from '../lib/api.ts'
import {
  formatUnitPrice,
  parseTypedPrice,
  toWireAmount,
} from '../lib/price-format.ts'

/**
 * The delivery.
 *
 * The one screen in this module with no counterpart on the sales side, and the
 * reason the module exists: this is where stock arrives carrying a cost, and
 * where a product finally knows what it is worth.
 *
 * Two things it deliberately does NOT do:
 *
 *   - It does not pre-fill the quantities with what was ordered. A receipt
 *     screen that starts filled in is a receipt screen somebody confirms
 *     without counting, and the whole value of the document is that a person
 *     looked at the boxes. The "Receber tudo" button is there for the day it
 *     really did all arrive, as one deliberate act.
 *   - It does not clamp an over-receipt. `checkReceipt` from `@aeris/purchasing`
 *     — the same function the server runs — refuses it by name, because somebody
 *     sending more than was agreed is a conversation, not a rounding error.
 */

export type PurchaseReceiptDialogProps = {
  document: PurchaseDetail
  onClose: () => void
  /**
   * O que chegou AGORA, linha a linha, depois que o servidor aceitou (F124).
   *
   * Para a tela de compras oferecer as etiquetas do que acabou de entrar. É a
   * quantidade digitada neste recebimento — nunca a pedida: recebimento
   * parcial é normal, e etiquetar o que não chegou é colar adesivo em peça que
   * não está na loja.
   */
  onReceived?:
    | ((lines: readonly { variantId: string; typedQuantity: string }[]) => void)
    | undefined
}

type Entry = {
  /** What is arriving now, as typed. */
  readonly quantity: string
  /** The invoice's cost, when it disagrees with what was ordered. */
  readonly unitCost: string
}

export const PurchaseReceiptDialog = ({
  document,
  onClose,
  onReceived,
}: PurchaseReceiptDialogProps): ReactNode => {
  const queryClient = useQueryClient()

  const [entries, setEntries] = useState<Record<string, Entry>>(() =>
    Object.fromEntries(
      document.lines.map((line) => [line.id, { quantity: '', unitCost: '' }])
    )
  )
  const [supplierDocumentNumber, setSupplierDocumentNumber] = useState(
    document.supplierDocumentNumber ?? ''
  )
  /*
   * The payment terms, raised only when this delivery finishes the purchase.
   *
   * Off by default. A shop that pays on delivery has no título to keep, and a
   * checkbox that starts ticked would fill the financeiro with debts that were
   * settled at the counter before anybody looked at the screen.
   */
  const [raisePayable, setRaisePayable] = useState(false)
  const [instalments, setInstalments] = useState('1')
  const [firstDueAt, setFirstDueAt] = useState(() => {
    const target = new Date(Date.now() + 30 * 86_400_000)
    const pad = (value: number): string => String(value).padStart(2, '0')
    return `${String(target.getFullYear())}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`
  })
  const [error, setError] = useState<string | null>(null)

  const setEntry = (lineId: string, patch: Partial<Entry>): void => {
    setEntries((current) => ({
      ...current,
      [lineId]: {
        quantity: patch.quantity ?? current[lineId]?.quantity ?? '',
        unitCost: patch.unitCost ?? current[lineId]?.unitCost ?? '',
      },
    }))
  }

  /**
   * The same check the server will run, run here first.
   *
   * Not instead of the server's — the API refuses independently, which is what
   * actually enforces the rule. This only means somebody who typed 11 where 10
   * was ordered finds out while looking at the line rather than after pressing
   * a button.
   */
  const check = useMemo(
    () =>
      checkReceipt(
        document.lines.map((line) => ({
          quantity: parseQuantity(line.quantity) ?? ZERO_MONEY,
          receivedQuantity: parseQuantity(line.receivedQuantity) ?? ZERO_MONEY,
          receivingNow:
            parseQuantity(
              (entries[line.id]?.quantity ?? '').replace(',', '.')
            ) ?? ZERO_MONEY,
        }))
      ),
    [document.lines, entries]
  )

  const anything = document.lines.some((line) => {
    const typed = parseQuantity(
      (entries[line.id]?.quantity ?? '').replace(',', '.')
    )
    return typed !== null && typed.units > 0n
  })

  const receive = useMutation({
    mutationFn: async () => {
      const lines = document.lines
        .map((line) => {
          const entry = entries[line.id]
          const typed = parseQuantity((entry?.quantity ?? '').replace(',', '.'))
          if (typed === null || typed.units <= 0n) return null

          const cost = parseTypedPrice(entry?.unitCost ?? '')

          return {
            lineId: line.id,
            quantity: toWireAmount(typed),
            ...(cost === null ? {} : { unitCost: toWireAmount(cost) }),
          }
        })
        .filter((line): line is NonNullable<typeof line> => line !== null)

      if (lines.length === 0) {
        throw new ApiError(
          422,
          'nothing_received',
          'Informe a quantidade de ao menos um item.'
        )
      }

      return await receivePurchase(document.id, {
        version: document.version,
        supplierDocumentNumber:
          supplierDocumentNumber.trim().length === 0
            ? null
            : supplierDocumentNumber.trim(),
        lines,
        // Only meaningful on the delivery that completes the purchase — the
        // server enforces that; the screen only offers it when it applies.
        ...(raisePayable && check.ok && check.fullyReceived
          ? {
              payment: {
                instalmentCount: Math.max(
                  1,
                  Number.parseInt(instalments, 10) || 1
                ),
                firstDueAt: new Date(`${firstDueAt}T12:00:00`).toISOString(),
                interval: 'monthly' as const,
                methodId: null,
              },
            }
          : {}),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['purchases'] })
      // The stock screens are now wrong on screen: the balances moved and so
      // did the average cost.
      await queryClient.invalidateQueries({ queryKey: ['stock-balances'] })
      // And the financeiro, if this receipt raised the debt for it.
      await queryClient.invalidateQueries({ queryKey: ['finance'] })
      // Antes de fechar: `entries` ainda tem o que foi digitado neste
      // recebimento, que é o que as etiquetas precisam.
      onReceived?.(
        document.lines.map((line) => ({
          variantId: line.variantId,
          typedQuantity: entries[line.id]?.quantity ?? '',
        }))
      )
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível registrar o recebimento.'
      )
    },
  })

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault()
    setError(null)
    receive.mutate()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Receber compra${document.number === null ? '' : ` nº ${formatDocumentNumber(document.number)}`}`}
        description="Informe o que realmente chegou. O que não chegou fica pendente — a compra só encerra quando todos os itens vierem."
        className="max-w-3xl"
      >
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field
              label="Nota do fornecedor"
              hint="O número impresso na nota que veio com a mercadoria."
              className="w-64"
            >
              {(field) => (
                <Input
                  {...field}
                  value={supplierDocumentNumber}
                  onChange={(event) => {
                    setSupplierDocumentNumber(event.target.value)
                  }}
                />
              )}
            </Field>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setEntries((current) =>
                  Object.fromEntries(
                    document.lines.map((line) => [
                      line.id,
                      {
                        quantity: line.outstandingQuantity,
                        unitCost: current[line.id]?.unitCost ?? '',
                      },
                    ])
                  )
                )
              }}
            >
              Receber tudo que falta
            </Button>
          </div>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Produto</TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    Pedido
                  </TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    Já recebido
                  </TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    Falta
                  </TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    Recebendo agora
                  </TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    Custo da nota
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {document.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <span className="block">{line.description}</span>
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {line.unit} · pedido a {formatUnitPrice(line.unitCost)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-(--font-mono)">
                      {line.quantity.replace(/\.?0+$/, '')}
                    </TableCell>
                    <TableCell className="text-right font-(--font-mono) text-(--text-secondary)">
                      {line.receivedQuantity.replace(/\.?0+$/, '')}
                    </TableCell>
                    <TableCell className="text-right font-(--font-mono)">
                      {line.outstandingQuantity.replace(/\.?0+$/, '')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        value={entries[line.id]?.quantity ?? ''}
                        inputMode="decimal"
                        aria-label={`Quantidade recebida de ${line.description}`}
                        className="ml-auto w-24 text-right"
                        onChange={(event) => {
                          setEntry(line.id, { quantity: event.target.value })
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Empty keeps the ordered cost. Filling it in says the
                          invoice charged something else — the negotiated
                          number is a plan, the invoice is what happened. */}
                      <Input
                        value={entries[line.id]?.unitCost ?? ''}
                        inputMode="decimal"
                        placeholder="igual ao pedido"
                        aria-label={`Custo da nota para ${line.description}`}
                        className="ml-auto w-32 text-right"
                        onChange={(event) => {
                          setEntry(line.id, { unitCost: event.target.value })
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {check.ok ? (
                <Badge tone={check.fullyReceived ? 'success' : 'warning'}>
                  {check.fullyReceived
                    ? 'Encerra a compra'
                    : 'Recebimento parcial — a compra continua aberta'}
                </Badge>
              ) : (
                <Badge tone="danger">
                  {check.failure.reason === 'exceeds-ordered'
                    ? `O item ${String(check.failure.index + 1)} está recebendo mais do que foi pedido.`
                    : `A quantidade do item ${String(check.failure.index + 1)} não pode ser negativa.`}
                </Badge>
              )}
            </div>

            {/* Read from the document rather than recomputed here: the header
                total is what the supplier is owed, and it does not change
                because part of the delivery is late. */}
            <span className="text-(length:--text-xs) text-(--text-muted)">
              Total da compra{' '}
              <span className="font-(--font-mono) text-(--text-secondary)">
                {formatMoney(
                  parseTypedPrice(document.totalAmount.replace('.', ',')) ??
                    ZERO_MONEY
                )}
              </span>
            </span>
          </div>

          {/* Offered only when this delivery finishes the purchase: the debt is
              for the whole invoice, and raising it on the first of three
              deliveries would put the shop on the hook for goods that have not
              turned up. */}
          {check.ok && check.fullyReceived ? (
            <div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2">
              <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text-secondary)">
                <input
                  type="checkbox"
                  checked={raisePayable}
                  onChange={(event) => {
                    setRaisePayable(event.target.checked)
                  }}
                />
                Gerar contas a pagar desta compra
              </label>

              {raisePayable ? (
                <div className="flex flex-wrap gap-3">
                  <Field label="Parcelas" className="w-28">
                    {(field) => (
                      <Input
                        {...field}
                        inputMode="numeric"
                        value={instalments}
                        onChange={(event) => {
                          setInstalments(event.target.value)
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Primeiro vencimento" className="w-44">
                    {(field) => (
                      <Input
                        {...field}
                        type="date"
                        value={firstDueAt}
                        onChange={(event) => {
                          setFirstDueAt(event.target.value)
                        }}
                      />
                    )}
                  </Field>
                  <p className="self-end text-(length:--text-xs) text-(--text-muted)">
                    Mercadoria e dívida entram juntas, na mesma transação.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={receive.isPending || !check.ok || !anything}
              leadingIcon={
                <PackageCheck aria-hidden="true" className="h-4 w-4" />
              }
            >
              Registrar recebimento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
